import { medcheckPrescriptionTrainingDatabase } from '../data/medcheckPrescriptionTrainingDatabase';
import type { Medicine } from '../types';
import { enhanceMedicineInstructions, enhanceMedicinesWithInstructions } from '../utils/prescriptionInstructions';
import { applyTrainingDbMedicineMatch, normalizeMedicineName } from './medicineResolver';

type TimeSlot = 'morning' | 'noon' | 'evening' | 'night';

interface TrainingDatabase {
  timeSlotRules?: {
    patterns?: Record<string, readonly TimeSlot[]>;
    foodTimingPatterns?: Record<string, string>;
  };
}

export interface PrescriptionCorrectionResult {
  medicines: Medicine[];
  validationWarnings: string[];
}

const DB = medcheckPrescriptionTrainingDatabase as unknown as TrainingDatabase;
const UNKNOWN_VALUES = new Set(['', 'not specified', 'needs review', 'needs verification', 'unknown', 'n/a', 'na']);

function hasMeaningfulValue(value?: string): boolean {
  return !UNKNOWN_VALUES.has((value || '').trim().toLowerCase());
}

function normalizeBanglaDigits(text: string): string {
  return text.replace(/[\u09E6-\u09EF]/g, (digit) => String(digit.charCodeAt(0) - 0x09E6));
}

function normalizeFrequencyPattern(value?: string): string {
  const normalized = normalizeBanglaDigits(value || '');
  const match = normalized.match(/([0-9]+)\s*\+\s*([0-9]+)\s*\+\s*([0-9]+)/);
  return match ? `${match[1]}+${match[2]}+${match[3]}` : '';
}

function inferTimeSlots(medicine: Medicine): TimeSlot[] {
  const source = [
    medicine.frequency,
    medicine.frequencyLabel,
    medicine.rawInstruction,
    medicine.instruction,
    medicine.timing,
    medicine.notes,
  ].filter(Boolean).join(' ');
  const normalized = normalizeBanglaDigits(source).toLowerCase();
  const frequencyPattern = normalizeFrequencyPattern(source);
  const patternSlots = frequencyPattern ? DB.timeSlotRules?.patterns?.[frequencyPattern] : undefined;
  const slots = new Set<TimeSlot>(patternSlots ? Array.from(patternSlots) : []);

  if (/morning|breakfast|সকাল|নাস্তা/.test(normalized)) slots.add('morning');
  if (/noon|lunch|দুপুর/.test(normalized)) slots.add('noon');
  if (/evening|সন্ধ্যা|বিকাল/.test(normalized)) slots.add('evening');
  if (/night|bedtime|রাত/.test(normalized)) slots.add('night');
  if (/8\s*(hour|hr)|৮\s*ঘণ্টা/.test(normalized)) {
    slots.add('morning');
    slots.add('noon');
    slots.add('evening');
    slots.add('night');
  }
  if (/12\s*(hour|hr)|১২\s*ঘণ্টা/.test(normalized)) {
    slots.add('morning');
    slots.add('night');
  }

  return Array.from(slots);
}

function normalizeFoodTiming(medicine: Medicine): string {
  const source = [medicine.timing, medicine.rawInstruction, medicine.instruction, medicine.notes]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  for (const [pattern, value] of Object.entries(DB.timeSlotRules?.foodTimingPatterns || {})) {
    if (source.includes(pattern.toLowerCase())) {
      if (value === 'after_food') return 'after food';
      if (value === 'before_food') return 'before food';
      if (value === '30_min_before_food') return '30 minutes before food';
      return value;
    }
  }

  if (/after\s*(food|meal)|খাবার\s*পর|খাবার\s*পরে/.test(source)) return 'after food';
  if (/before\s*(food|meal)|খাবার\s*আগে|পূর্বে/.test(source)) return 'before food';
  return medicine.timing || '';
}

function mergeCorrection(medicine: Medicine): Medicine {
  const resolved = applyTrainingDbMedicineMatch({
    ...medicine,
    name: normalizeMedicineName(medicine.name) || medicine.name,
  });
  const timeSlots = inferTimeSlots(resolved);
  const timing = hasMeaningfulValue(resolved.timing) ? resolved.timing : normalizeFoodTiming(resolved);
  const frequencyPattern = normalizeFrequencyPattern(resolved.frequency || resolved.rawInstruction || '');

  return enhanceMedicineInstructions({
    ...resolved,
    frequency: hasMeaningfulValue(resolved.frequency) ? resolved.frequency : frequencyPattern || resolved.frequency,
    timing,
    timeSlots: timeSlots.length > 0 ? timeSlots : resolved.timeSlots,
  });
}

function getValidationWarnings(medicines: Medicine[]): string[] {
  const warnings: string[] = [];
  const unresolved = medicines.filter((medicine) => medicine.identificationStatus !== 'identified');
  if (unresolved.length > 0) {
    warnings.push(`${unresolved.length} medicine${unresolved.length === 1 ? '' : 's'} need identity confirmation.`);
  }

  const missingSchedule = medicines.filter(
    (medicine) => !hasMeaningfulValue(medicine.frequency) && !hasMeaningfulValue(medicine.frequencyLabel)
  );
  if (missingSchedule.length > 0) {
    warnings.push(`${missingSchedule.length} medicine${missingSchedule.length === 1 ? '' : 's'} need schedule confirmation.`);
  }

  return warnings;
}

export function correctPrescriptionExtraction(medicines: Medicine[], _ocrText = ''): PrescriptionCorrectionResult {
  const corrected = enhanceMedicinesWithInstructions(medicines.map(mergeCorrection));
  const validationWarnings = getValidationWarnings(corrected);

  if (import.meta.env.DEV) {
    const identified = corrected.filter((medicine) => medicine.identificationStatus === 'identified').length;
    console.info('[MedCheck Correction] local resolver applied', {
      total: corrected.length,
      identified,
      unresolved: corrected.length - identified,
      validationWarnings,
    });
  }

  return { medicines: corrected, validationWarnings };
}
